import { useState } from "react";
import { api, setToken } from "../lib/api";
import { isProductionConsole } from "../lib/appMode.js";
import { getItem, removeItem, setItem } from "../lib/safeStorage";
import { getToken } from "../lib/api";

const SESSION_KEY = "wecrew_session";
export const PENDING_CHAT_KEY = "wecrew_pending_chat";

/** Prod requires JWT; dev allows demo/local session without token. */
export function hasSession() {
  if (getToken()) return true;
  if (isProductionConsole) return false;
  return Boolean(getItem(SESSION_KEY));
}

export function clearSession() {
  removeItem(SESSION_KEY);
  setToken(null);
}

function BrandMark() {
  return (
    <div className="mp-brand">
      <div className="mp-logo"><span>W</span></div>
      <div className="mp-brand-name">We<em>crew</em> Copilot</div>
    </div>
  );
}

function AuthLeft({ title, children }) {
  return (
    <aside className="mp-left">
      <div className="mp-glow" />
      <div className="mp-glow2" />
      <div className="mp-grid" />
      <BrandMark />
      <div className="mp-left-main">
        <h1 className="mp-left-h" dangerouslySetInnerHTML={{ __html: title }} />
        {children}
      </div>
      <div className="mp-foot">Self-hosted · Kind / K3s · Enterprise L2</div>
    </aside>
  );
}

function Field({ label, type = "text", value, onChange, placeholder, autoComplete }) {
  return (
    <label className="mp-field">
      <span>{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required
      />
    </label>
  );
}

export function SignUpPage({ onSuccess, onNavigate }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    workspace: "DevOps / Platform",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const strength = (() => {
    const p = form.password || "";
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
    if (/\d/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  })();

  const finish = (token) => {
    if (token) setToken(token);
    setItem(SESSION_KEY, "1");
    onSuccess();
  };

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      const name = `${form.firstName} ${form.lastName}`.trim();
      if (name.length < 2) {
        throw new Error("Enter your first and last name (at least 2 characters)");
      }
      const data = await api.register(form.email, form.password, name);
      setStep(3);
      finish(data?.access_token);
    } catch (err) {
      setStep(2);
      setError(err.message || "Signup failed");
      if (!isProductionConsole) {
        setError((prev) => prev || err.message || "Signup failed — continuing in local demo mode");
        setItem(SESSION_KEY, "local");
        setTimeout(() => onSuccess(), 700);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mp-auth">
      <AuthLeft title={'Your L2 knowledge<br/>copilot, <em>ready.</em>'}>
        <div className="mp-steps">
          {[
            ["01", "Ask ops & ITSM questions", "Incidents, K8s, runbooks, DevOps tools"],
            ["02", "Ground answers in your KB", "SOPs, PDFs, URLs — chunked and indexed"],
            ["03", "Escalate with confidence", "Six-panel answers + references"],
            ["04", "Observe & integrate", "Logs, CMDB, ServiceNow, Slack, more"],
          ].map(([n, t, s]) => (
            <div className="mp-step" key={n}>
              <div className="mp-step-num">{n}</div>
              <div>
                <div className="mp-step-title">{t}</div>
                <div className="mp-step-sub">{s}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mp-free">
          <i />
          <div>
            <div className="mp-free-t">Self-hosted on your cluster — no SaaS lock-in</div>
            <div className="mp-free-s">kt.copilot.wecrew.in · enterprise L2 stack</div>
          </div>
        </div>
      </AuthLeft>

      <section className="mp-right">
        <div className="mp-card">
          <button type="button" className="mp-back-link" onClick={() => onNavigate("landing")}>← Back to home</button>
          <h2 className="mp-form-h">Create your account</h2>
          <p className="mp-form-sub">
            Already have one?{" "}
            <button type="button" onClick={() => onNavigate("signin")}>Sign in →</button>
          </p>

          <div className="mp-progress">
            {[1, 2, 3].map((n, i) => (
              <div key={n} className="mp-prog-wrap">
                {i > 0 && <div className={`mp-prog-line ${step > i ? "done" : ""}`} />}
                <div className="mp-prog-step">
                  <div className={`mp-prog-dot ${step > n ? "done" : step === n ? "active" : "inactive"}`}>
                    {step > n ? "✓" : n}
                  </div>
                  <span className={`mp-prog-label ${step >= n ? "active" : "inactive"}`}>
                    {n === 1 ? "Account" : n === 2 ? "Workspace" : "Done"}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {step === 1 && (
            <div className="mp-slide">
              <div className="mp-row2">
                <Field label="First name" value={form.firstName} onChange={(v) => set("firstName", v)} placeholder="Raj" autoComplete="given-name" />
                <Field label="Last name" value={form.lastName} onChange={(v) => set("lastName", v)} placeholder="Madhu" autoComplete="family-name" />
              </div>
              <Field label="Work email" type="email" value={form.email} onChange={(v) => set("email", v)} placeholder="you@wecrew.in" autoComplete="email" />
              <Field label="Password" type="password" value={form.password} onChange={(v) => set("password", v)} placeholder="Min. 8 characters" autoComplete="new-password" />
              <div className="mp-pw">{[1, 2, 3, 4].map((i) => <i key={i} className={strength >= i ? "on" : ""} />)}</div>
              <button type="button" className="mp-submit" disabled={!form.email || form.password.length < 8} onClick={() => setStep(2)}>
                Continue →
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="mp-slide">
              <Field label="Workspace name" value={form.workspace} onChange={(v) => set("workspace", v)} placeholder="Wecrew Ops" />
              <div className="mp-industries">
                {["DevOps / Platform", "SRE", "ITSM / L2", "Security"].map((ind) => (
                  <button key={ind} type="button" className={`mp-ind ${form.workspace === ind ? "selected" : ""}`} onClick={() => set("workspace", ind)}>
                    {ind}
                  </button>
                ))}
              </div>
              <button type="button" className="mp-submit" disabled={busy} onClick={submit}>
                {busy ? <span className="mp-spin" /> : "Create account →"}
              </button>
              {error && <p className="mp-err">{error}</p>}
              <button type="button" className="mp-back" onClick={() => setStep(1)}>← Back</button>
            </div>
          )}

          {step === 3 && (
            <div className="mp-success">
              <div className="mp-success-icon">
                <svg viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg>
              </div>
              <h3>You&apos;re in</h3>
              <p>Opening Wecrew Copilot AI…</p>
              {error && <p className="mp-err">{error}</p>}
            </div>
          )}

          {!isProductionConsole && (
            <button
              type="button"
              className="mp-demo"
              onClick={() => {
                setItem(SESSION_KEY, "demo");
                onSuccess();
              }}
            >
              Continue without account (demo)
            </button>
          )}
          <p className="mp-terms">By continuing you agree to Wecrew terms for this internal stack.</p>
        </div>
      </section>
    </div>
  );
}

export function SignInPage({ onSuccess, onNavigate }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("support@wecrew.in");
  const [password, setPassword] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    setBusy(true);
    try {
      const data = await api.login(email, password);
      if (data?.access_token) setToken(data.access_token);
      setItem(SESSION_KEY, "1");
      onSuccess();
    } catch (err) {
      setError(err.message || "Sign in failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mp-auth">
      <AuthLeft title={'Welcome back<br/>to the <em>console.</em>'}>
        <p className="mp-left-desc">
          Sign in to chat with your knowledge base, triage incidents, and run L2 playbooks —
          grounded answers with confidence and escalation paths.
        </p>
        <div className="mp-mini">
          <div className="mp-mini-bar">
            <i style={{ background: "#ff5b2e" }} />
            <i style={{ background: "#2b4cff" }} />
            <i style={{ background: "#0f7a55" }} />
          </div>
          <div className="mp-mini-body">
            <div className="mp-mini-kpis">
              {[
                ["Open incidents", "12", "−18%"],
                ["KB queries", "2.8k", "+12%"],
                ["Confidence", "94%", "avg"],
              ].map(([l, v, t]) => (
                <div className="mp-mini-kpi" key={l}>
                  <div className="mp-mini-l">{l}</div>
                  <div className="mp-mini-v">{v}</div>
                  <div className="mp-mini-t">{t}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </AuthLeft>

      <section className="mp-right">
        <form className="mp-card" onSubmit={submit}>
          <button type="button" className="mp-back-link" onClick={() => onNavigate("landing")}>← Back to home</button>
          <h2 className="mp-form-h">Sign in</h2>
          <p className="mp-form-sub">
            New here?{" "}
            <button type="button" onClick={() => onNavigate("signup")}>Create account →</button>
          </p>
          <Field label="Work email" type="email" value={email} onChange={setEmail} placeholder="support@wecrew.in" autoComplete="email" />
          <Field label="Password" type="password" value={password} onChange={setPassword} placeholder="Your password" autoComplete="current-password" />
          {isProductionConsole && (
            <p className="mp-form-sub" style={{ marginTop: -4 }}>
              Default admin account: <strong>support@wecrew.in</strong>
            </p>
          )}
          {error && <p className="mp-err">{error}</p>}
          <button type="submit" className="mp-submit" disabled={busy || !email || !password}>
            {busy ? <span className="mp-spin" /> : "Sign in →"}
          </button>
          {!isProductionConsole && (
            <button
              type="button"
              className="mp-demo"
              onClick={() => {
                setItem(SESSION_KEY, "demo");
                onSuccess();
              }}
            >
              Continue without account (demo)
            </button>
          )}
          <p className="mp-terms">Protected internal console · Wecrew Copilot AI</p>
        </form>
      </section>
    </div>
  );
}

/** @deprecated use SignUpPage / SignInPage */
export function AuthGate({ onEnter, initialMode = "signup" }) {
  const [mode, setMode] = useState(initialMode);
  if (mode === "signin") {
    return (
      <SignInPage
        onSuccess={onEnter}
        onNavigate={(r) => (r === "signup" ? setMode("signup") : null)}
      />
    );
  }
  return (
    <SignUpPage
      onSuccess={onEnter}
      onNavigate={(r) => (r === "signin" ? setMode("signin") : null)}
    />
  );
}
